import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Notification } from "../../domain/entities/Notification";
import { NotificationUseCases } from "../../domain/usecases/NotificationUseCases";
import { FirebaseNotificationRepository } from "../repositories/FirebaseNotificationRepository";

export const useNotificationStore = defineStore(
  "notifications",
  () => {
    // Initial load of notifications from localStorage (if they exist)
    const notifications = ref<Notification[]>([]);
    const isInitialized = ref(false);
    const permissionStatus = ref<NotificationPermission>("default");
    const showPermissionModal = ref(false);
    const firebaseToken = ref("");

    const unreadCount = computed(
      () => notifications.value.filter((n) => !n.read).length
    );

    const repository = new FirebaseNotificationRepository();
    const useCases = new NotificationUseCases(repository);

    async function requestPermission(): Promise<boolean> {
      try {
        const permission = await Notification.requestPermission();
        permissionStatus.value = permission;
        showPermissionModal.value = false;
        return permission === "granted";
      } catch (error) {
        console.error("Error requesting permission:", error);
        return false;
      }
    }

    async function checkPermission(): Promise<void> {
      permissionStatus.value = Notification.permission;
      if (permissionStatus.value === "default") {
        showPermissionModal.value = true;
      }
    }

    async function initializeNotifications(userId: string): Promise<void> {
      try {
        // Check permissions first
        await checkPermission();
        if (permissionStatus.value !== "granted") {
          showPermissionModal.value = true;
          return;
        }

        // ✅ Ensure the Service Worker is registered and active
        if (!("serviceWorker" in navigator)) {
          throw new Error("Browser does not support Service Workers");
        }

        // Register the Service Worker if not registered
        let registration: ServiceWorkerRegistration;
        try {
          registration = await navigator.serviceWorker.ready;
          if (!registration.active) {
            throw new Error("Service Worker not active");
          }
        } catch (error) {
          // Try to register if it does not exist
          registration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js"
          );

          // Wait for it to be active
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "activated") {
                }
              });
            }
          });
        }

        // ✅ Get the token with the Service Worker
        const token = await useCases.getFirebaseToken(
          "BKcOx_rHzxOed9UUQux8J2KqhVhAKYIxyi-FxO6rChcpMLEkXkqjXe8EDL5FZd9-15YewxyzzEXciqAO-m7VfAI",
          registration // 👈 Pass the registration to the use case
        );
        firebaseToken.value = token;

        // Set up listeners
        useCases.onMessage((payload) => {
          addNotification(payload);

          // Show notification in the foreground
          if (Notification.permission === "granted") {
            new Notification(
              payload.notification?.title || "New notification",
              {
                body: payload.notification?.body,
                icon: "/favicon.svg",
              }
            );
          }
        });

        isInitialized.value = true;
      } catch (error) {
        console.error("Error initializing notifications:", error);
        throw error;
      }
    }

    function markAsRead(notificationId: string): void {
      const notification = notifications.value.find(
        (n) => n.id === notificationId
      );
      if (notification) {
        notification.read = true;
      }
    }

    function deleteNotification(notificationId: string): void {
      notifications.value = notifications.value.filter(
        (n) => n.id !== notificationId
      );
    }

    function clearAllNotifications(): void {
      notifications.value = [];
      // Clear notifications from localStorage
    }

    function addNotification(payload: any): void {
      const newNotification: Notification = {
        id: Date.now().toString(),
        title: payload.title || payload.notification?.title || "",
        body: payload.body || payload.notification?.body || "",
        timestamp: new Date(),
        read: false,
        data: payload.data,
      };

      notifications.value.unshift(newNotification);
    }

    return {
      notifications,
      unreadCount,
      isInitialized,
      permissionStatus,
      showPermissionModal,
      requestPermission,
      checkPermission,
      initializeNotifications,
      markAsRead,
      deleteNotification,
      addNotification,
      clearAllNotifications,
      firebaseToken,
    };
  },
  {
    persist: true,
  }
);
